/**
 * Custom toggle-switch renderers for SurveyJS boolean and checkbox questions.
 * - BooleanSlider: renders a boolean question as a toggle switch
 * - CheckboxSlider: renders a checkbox question as a list of toggle switches
 */
import React from "react";
import { Serializer, RendererFactory } from "survey-core";
import { SurveyQuestionBoolean, SurveyQuestionCheckbox, ReactQuestionFactory } from "survey-react-ui";
import DOMPurify from 'dompurify';
import "../styles/BooleanSlider.scss";

// Register a custom renderAs property on the boolean question type
Serializer.addProperty("boolean", {
    name: "renderAs",
    default: "default",
    choices: ["default", "toggle-switch", "checkbox"]
});

// Register a custom renderAs property on the checkbox question type
Serializer.addProperty("checkbox", {
    name: "renderAs",
    default: "default",
    choices: ["default", "toggle-switch"]
});

class BooleanSlider extends SurveyQuestionBoolean {
    handleToggle = () => {
        if (this.isDisplayMode) return;
        this.question.value = !this.question.value;
    };

    renderElement() {
        const isChecked = !!this.question.value;
        const isDisabled = this.isDisplayMode;

        return (
            <div className={`boolean-slider-wrapper ${isDisabled ? "disabled" : ""}`}>
                <button
                    type="button"
                    role="switch"
                    aria-checked={isChecked}
                    aria-label={this.question.title}
                    className={`boolean-slider-track ${isChecked ? "on" : "off"}`}
                    onClick={this.handleToggle}
                    disabled={isDisabled}
                    id={this.question.inputId}
                >
                    <span className="boolean-slider-thumb" />
                </button>
            </div>
        );
    }
}

class CheckboxSlider extends SurveyQuestionCheckbox {
    handleToggle = (choiceValue) => {
        if (this.isDisplayMode) return;
        const question = this.question;
        const item = question.visibleChoices.find(c => c.value === choiceValue);
        if (item) {
            if (question.isItemSelected(item)) {
                const newVal = (question.value || []).filter(v => v !== choiceValue);
                question.value = newVal.length > 0 ? newVal : undefined;
                // When the "Other" toggle is switched OFF, discard any free text the
                // user had typed so a stale value isn't kept (or resubmitted if they
                // toggle it back on). Clear both SurveyJS's comment and our local state.
                if (this.isOtherChoice(item)) {
                    question.comment = "";
                    this.setState({ otherComment: "" });
                }
            } else {
                const newVal = [...(question.value || []), choiceValue];
                question.value = newVal;
            }
        }
        this.setState({ renderCount: (this.state?.renderCount || 0) + 1 });
    };

    // Override to suppress default checkbox item rendering
    renderItems() {
        return null;
    }

    // Update the free-text "Other" comment as the user types.
    //
    // SurveyJS's Question.comment setter trims the string (and its onValueChanged
    // round-trip re-normalises it), so binding the input's `value` directly to
    // `question.comment` would strip a space the instant it is typed — making it look
    // like the field rejects spaces. To avoid that we hold the raw keystrokes in local
    // component state (otherComment) and use it as the controlled value. The trimmed
    // canonical value still lives on question.comment for save/submit; we only defer to
    // SurveyJS's trimming rather than applying it mid-typing.
    handleOtherComment = (e) => {
        if (this.isDisplayMode) return;
        const raw = e.target.value;
        this.question.comment = raw;
        this.setState({ otherComment: raw });
    };

    // The value shown in the input: prefer the raw local keystrokes when present,
    // otherwise fall back to whatever SurveyJS holds (e.g. a prepopulated value).
    getOtherCommentValue() {
        return this.state?.otherComment !== undefined
            ? this.state.otherComment
            : (this.question.comment || "");
    }

    // Is this choice the special "Other" item?
    isOtherChoice(choice) {
        return this.question.hasOther &&
            this.question.otherItem &&
            choice.value === this.question.otherItem.value;
    }

    // Render a single toggle item (used by both grouped and ungrouped paths).
    renderChoiceItem(choice, isDisabled, jsonObj, isOptionDescriptionVisible) {
        const isChecked = this.question.isItemSelected(choice);
        const matchedChoice = jsonObj.choices?.find(c => c.value === choice.value);
        const description = isOptionDescriptionVisible ? matchedChoice?.description : null;

        // The "Other" toggle additionally shows a free-text input when selected.
        const isOther = this.isOtherChoice(choice);
        const showOtherInput = isOther && this.question.isOtherSelected;

        return (
            <div key={choice.value} className="checkbox-slider-item">
                <div className="checkbox-slider-row">
                    <button
                        type="button"
                        role="switch"
                        aria-checked={isChecked}
                        aria-label={choice.text || choice.value}
                        className={`boolean-slider-track ${isChecked ? "on" : "off"}`}
                        onClick={() => this.handleToggle(choice.value)}
                        disabled={isDisabled}
                    >
                        <span className="boolean-slider-thumb" />
                    </button>
                    <span
                        className="checkbox-slider-label"
                        onClick={isDisabled ? undefined : () => this.handleToggle(choice.value)}
                    >
                        {choice.text || choice.value}
                    </span>
                </div>
                {description && (
                    <div
                        className="newsletter-option-description"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
                    />
                )}
                {showOtherInput && (
                    <input
                        type="text"
                        className="checkbox-slider-other-input sd-input sd-comment"
                        aria-label={this.question.otherPlaceholder || (choice.text || "Other")}
                        placeholder={this.question.otherPlaceholder || ""}
                        value={this.getOtherCommentValue()}
                        onChange={this.handleOtherComment}
                        disabled={isDisabled}
                    />
                )}
            </div>
        );
    }

    renderElement() {
        const isDisabled = this.isDisplayMode;
        const choices = this.question.visibleChoices || [];
        const jsonObj = this.question.jsonObj || {};
        const isOptionDescriptionVisible = jsonObj.isOptionDescriptionVisible;

        // Deduplicate choices by value to prevent rendering duplicates
        const seen = new Set();
        const uniqueChoices = choices.filter(choice => {
            if (seen.has(choice.value)) return false;
            seen.add(choice.value);
            return true;
        });

        // The special "Other" item is injected by SurveyJS into visibleChoices (it is
        // not part of jsonObj.choices, so it has no category). Pull it out of the normal
        // choice flow and render it separately at the end, full-width, so it sits below
        // both the flat list and the grouped/two-column layouts cleanly and its free-text
        // input never gets squeezed into a category column.
        const otherItemValue = this.question.hasOther ? this.question.otherItem?.value : undefined;
        const regularChoices = otherItemValue !== undefined
            ? uniqueChoices.filter(c => c.value !== otherItemValue)
            : uniqueChoices;
        const otherChoice = otherItemValue !== undefined
            ? uniqueChoices.find(c => c.value === otherItemValue)
            : undefined;

        const otherEl = otherChoice
            ? (
                <div className="checkbox-slider-other">
                    {this.renderChoiceItem(otherChoice, isDisabled, jsonObj, isOptionDescriptionVisible)}
                </div>
            )
            : null;

        // The standard (non-slider) renderer groups choices by category via DOM
        // manipulation in SurveyForm.js. The slider renderer owns its own DOM and
        // that logic never reaches it, so grouping is handled here instead.
        // The category for each choice comes from the raw JSON (jsonObj.choices),
        // matching the same source the standard grouping path reads.
        const categoryFor = (value) =>
            jsonObj.choices?.find(c => c.value === value)?.category || "";

        const hasCategories = regularChoices.some(c => categoryFor(c.value));

        // Ungrouped: flat list (original behaviour).
        if (!hasCategories) {
            return (
                <div className="checkbox-slider-container">
                    {regularChoices.map((choice) =>
                        this.renderChoiceItem(choice, isDisabled, jsonObj, isOptionDescriptionVisible)
                    )}
                    {otherEl}
                </div>
            );
        }

        // Grouped: preserve first-seen category order, then render each group with a
        // heading. Class names (category-group / module-subheading) match the standard
        // renderer so shared SCSS (grouping + two-column layout) applies consistently.
        const groupOrder = [];
        const groups = {};
        regularChoices.forEach((choice) => {
            const cat = categoryFor(choice.value);
            if (!groups[cat]) {
                groups[cat] = [];
                groupOrder.push(cat);
            }
            groups[cat].push(choice);
        });

        const slug = (cat) => cat.replace(/\s+/g, "-").replace(/[^a-zA-Z0-9\-]/g, "");

        return (
            <div className="checkbox-slider-container checkbox-slider-container--grouped">
                {groupOrder.map((cat) => (
                    <div
                        key={cat || "__ungrouped"}
                        className={`item-container category-group category-group-${slug(cat)}`}
                    >
                        {cat && <h5 className="module-subheading">{cat}</h5>}
                        {groups[cat].map((choice) =>
                            this.renderChoiceItem(choice, isDisabled, jsonObj, isOptionDescriptionVisible)
                        )}
                    </div>
                ))}
                {otherEl}
            </div>
        );
    }
}

// Register the boolean slider component
ReactQuestionFactory.Instance.registerQuestion("sv-boolean-slider", (props) => {
    return React.createElement(BooleanSlider, props);
});

// Register the checkbox slider component
ReactQuestionFactory.Instance.registerQuestion("sv-checkbox-slider", (props) => {
    return React.createElement(CheckboxSlider, props);
});

// Register as renderer for boolean questions with renderAs: "toggle-switch"
RendererFactory.Instance.registerRenderer(
    "boolean",
    "toggle-switch",
    "sv-boolean-slider"
);

// Register as renderer for checkbox questions with renderAs: "toggle-switch"
RendererFactory.Instance.registerRenderer(
    "checkbox",
    "toggle-switch",
    "sv-checkbox-slider"
);

export { BooleanSlider, CheckboxSlider };
